import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, resolveMediaUrl } from '@/lib/api';
import { SiteMasterEntryForm } from '@/components/site-master/SiteMasterEntryForm';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { siteMasterPaths } from '@/lib/siteMasterPaths';
import { formatEntryRelocateMessage, type EntryRelocateResponse } from '@/lib/entryRelocateMessage';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

const PER_PAGE = 10;
const PIN_PRESETS = [7, 15, 30] as const;
const TOAST_AUTO_DISMISS_MS = 4500;
const TOAST_ERROR_DISMISS_MS = 6500;

const inputClass =
  'w-full rounded-2xl border-2 border-slate-200 px-4 py-3.5 text-lg focus:border-teal-500 focus:outline-none focus:ring-4 focus:ring-teal-500/20';

const actionBtnClass =
  'flex flex-1 items-center justify-center gap-1.5 rounded-xl py-3 text-base font-semibold transition active:scale-[0.98] disabled:opacity-50';

function isSiteMasterToastError(text: string): boolean {
  return /失败|冲突|无权|无法|错误|active transaction/i.test(text);
}

function isMpUserEntry(src?: string): boolean {
  return src === 'mp_user';
}

function isMpUserAwaitingReview(row: { entry_source?: string; draft_status?: string }): boolean {
  return isMpUserEntry(row.entry_source) && row.draft_status === 'awaiting_review';
}

function rowToMoveSheet(row: Row | EntryDetail, fallbackSiteId: number): Row {
  return {
    id: row.id,
    site_id: row.site_id ?? fallbackSiteId,
    site_name: row.site_name,
    title_text: row.title_text,
    body_text: row.body_text,
    contact_phone: row.contact_phone,
    contact_wechat_id: row.contact_wechat_id,
    valid_end_date: row.valid_end_date,
    created_at: row.created_at,
    sold_out: row.sold_out,
    is_sticky: row.is_sticky,
    sticky_expired: row.sticky_expired,
    sticky_remaining_days: row.sticky_remaining_days,
    pin_status_text: row.pin_status_text,
  };
}

type AuditFilter = 'all' | 'mp_user_pending';

type Row = {
  id: number;
  site_id?: number;
  site_name?: string;
  entry_source?: string;
  draft_status?: string;
  draft_errmsg?: string;
  title_text: string | null;
  body_text: string | null;
  contact_phone: string | null;
  contact_wechat_id: string | null;
  valid_end_date: string;
  created_at: string;
  sold_out: boolean;
  is_sticky: boolean;
  sticky_expired?: boolean;
  sticky_remaining_days?: number;
  pin_status_text?: string;
};

type EntryImage = { id: number; local_path: string; sort_order: number };

type EntryDetail = Row & {
  valid_start_date: string;
  images?: EntryImage[];
  type_key?: string | null;
  type_label?: string | null;
};

export function SiteMasterEntriesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const entryParam = searchParams.get('entry');
  const { loading: sessionLoading, ok: sessionOk, currentSite, currentSiteId, sites } = useSiteMasterSession();
  const allSitesMode = currentSiteId === 0;
  const deepLinkHandledRef = useRef(0);
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [pendingMpUserCount, setPendingMpUserCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [auditFilter, setAuditFilter] = useState<AuditFilter>('all');
  const [err, setErr] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<EntryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const [pinSheetRow, setPinSheetRow] = useState<Row | null>(null);
  const [pinBusyId, setPinBusyId] = useState(0);
  const [soldBusyId, setSoldBusyId] = useState(0);
  const [moveSheetRow, setMoveSheetRow] = useState<Row | null>(null);
  const [movePendingSiteId, setMovePendingSiteId] = useState(0);
  const [moveBusyId, setMoveBusyId] = useState(0);
  const [reviewBusyId, setReviewBusyId] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        items: Row[];
        total: number;
        pending_mp_user_count?: number;
      }>('site_master_list_entries', {
        method: 'GET',
        query: {
          page,
          per_page: PER_PAGE,
          ...(appliedQ ? { q: appliedQ } : {}),
          ...(auditFilter === 'mp_user_pending' ? { audit: 'mp_user_pending' } : {}),
        },
      });
      if (r.ok) {
        setItems(r.items);
        setTotal(r.total);
        setPendingMpUserCount(typeof r.pending_mp_user_count === 'number' ? r.pending_mp_user_count : 0);
      } else {
        setErr('加载失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message !== 'SESSION_EXPIRED') {
        setErr('加载失败');
      }
    } finally {
      setLoading(false);
    }
  }, [page, appliedQ, auditFilter, currentSiteId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const flash = (location.state as { flash?: string } | null)?.flash;
    if (!flash) return;
    setToast(flash);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    setPage(1);
    void load();
  }, [location.state, location.pathname, location.search, navigate, load]);

  useEffect(() => {
    if (!toast) return;
    const ms = isSiteMasterToastError(toast) ? TOAST_ERROR_DISMISS_MS : TOAST_AUTO_DISMISS_MS;
    const id = window.setTimeout(() => setToast(''), ms);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    setPage(1);
  }, [currentSite?.id, auditFilter]);

  const openDetail = useCallback(async (id: number): Promise<boolean> => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setToast('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; entry?: EntryDetail; detail?: string; err?: string }>(
        'site_master_get_entry',
        {
          method: 'GET',
          query: { id },
        },
      );
      if (r.ok && r.entry) {
        setDetail(r.entry);
        return true;
      }
      setErr(r.detail || (r.err === 'not_found' ? '信息不存在或不在您管理的站点范围内' : '无法打开该信息'));
      setDetailOpen(false);
      return false;
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        return false;
      }
      setErr(e instanceof Error ? e.message : '无法打开该信息');
      setDetailOpen(false);
      return false;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionLoading || !sessionOk) return;
    const raw = reviewParam || entryParam;
    if (!raw) return;
    const id = parseInt(raw, 10);
    if (!Number.isFinite(id) || id < 1) return;
    if (deepLinkHandledRef.current === id) return;
    deepLinkHandledRef.current = id;
    if (reviewParam) {
      setAuditFilter('mp_user_pending');
    }
    void (async () => {
      const ok = await openDetail(id);
      if (ok) {
        const next = new URLSearchParams(searchParams);
        next.delete('review');
        next.delete('entry');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [reviewParam, entryParam, openDetail, searchParams, setSearchParams, sessionLoading, sessionOk]);

  function patchRow(updated: Partial<Row> & { id: number }) {
    setItems((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
    if (pinSheetRow?.id === updated.id) {
      setPinSheetRow((prev) => (prev ? { ...prev, ...updated } : prev));
    }
  }

  async function applyPin(entryId: number, pinDays: number) {
    setPinBusyId(entryId);
    setToast('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        entry?: Row;
        cleared?: boolean;
      }>('site_master_set_entry_pin', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId, pin_days: pinDays }),
      });
      if (r.ok) {
        if (r.entry) {
          patchRow(r.entry);
        } else {
          await load();
        }
        setPinSheetRow(null);
        setToast(pinDays < 1 ? '已取消置顶' : `已置顶 ${pinDays} 天`);
      } else {
        setToast(r.detail ?? r.err ?? '置顶设置失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setToast('置顶设置失败');
    } finally {
      setPinBusyId(0);
    }
  }

  async function toggleSold(row: Row) {
    const nextSold = !row.sold_out;
    setSoldBusyId(row.id);
    setToast('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        entry?: Row;
      }>('site_master_set_entry_sold_out', {
        method: 'POST',
        body: JSON.stringify({ entry_id: row.id, sold_out: nextSold }),
      });
      if (r.ok) {
        if (r.entry) {
          patchRow(r.entry);
        } else {
          await load();
        }
        setToast(nextSold ? '已标为已售' : '已恢复在售');
      } else {
        setToast(r.detail ?? r.err ?? '更新失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setToast('更新失败');
    } finally {
      setSoldBusyId(0);
    }
  }

  async function approveMpUserReview(entryId: number) {
    setReviewBusyId(entryId);
    setToast('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('site_master_approve_mp_user_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId }),
      });
      if (r.ok) {
        setToast('已通过审核，信息已展示');
        setDetailOpen(false);
        await load();
      } else {
        setToast(r.detail ?? r.err ?? '审核失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setToast('审核失败');
    } finally {
      setReviewBusyId(0);
    }
  }

  async function rejectMpUserReview(entryId: number) {
    if (!window.confirm('确定拒绝这条用户发布？拒绝后不会在小程序展示。')) return;
    setReviewBusyId(entryId);
    setToast('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('site_master_reject_mp_user_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId }),
      });
      if (r.ok) {
        setToast('已拒绝');
        setDetailOpen(false);
        await load();
      } else {
        setToast(r.detail ?? r.err ?? '操作失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setToast('操作失败');
    } finally {
      setReviewBusyId(0);
    }
  }

  async function saveDetail(values: {
    type_key: string;
    body_text: string;
    contact_phone: string;
    contact_wechat_id: string;
    image_local_paths: string[];
  }) {
    if (!detail) return;
    setSaving(true);
    setToast('');
    try {
      const r = await apiFetch<{ ok: boolean; detail?: string; err?: string }>('site_master_update_entry', {
        method: 'POST',
        body: JSON.stringify({
          entry_id: detail.id,
          ...values,
        }),
      });
      if (r.ok) {
        setToast('已保存');
        setDetailOpen(false);
        void load();
      } else {
        setToast(r.detail ?? '保存失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setToast('保存失败');
    } finally {
      setSaving(false);
    }
  }

  function openMoveSheetForRow(row: Row) {
    setMovePendingSiteId(0);
    setMoveSheetRow(row);
    setToast('');
  }

  function closeMoveSheet() {
    if (moveBusyId) return;
    setMoveSheetRow(null);
    setMovePendingSiteId(0);
  }

  async function moveEntryToSite(
    entryId: number,
    fromSiteId: number,
    newSiteId: number,
    options?: { skipConfirm?: boolean }
  ) {
    if (newSiteId < 1 || newSiteId === fromSiteId) return;
    const fromSite = sites.find((s) => s.id === fromSiteId);
    const target = sites.find((s) => s.id === newSiteId);
    const fromLabel = fromSite?.site_name ?? `站点 #${fromSiteId}`;
    const targetLabel = target?.site_name ?? `站点 #${newSiteId}`;
    if (
      !options?.skipConfirm &&
      !window.confirm(
        `确定将信息 #${entryId} 从「${fromLabel}」迁入「${targetLabel}」？\n将在目标站创建新信息，并删除原信息及其转发任务。`
      )
    ) {
      return;
    }
    setMoveBusyId(entryId);
    setToast('');
    try {
      const r = await apiFetch<EntryRelocateResponse>('site_master_move_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId, new_site_id: newSiteId }),
      });
      if (r.ok) {
        setMoveSheetRow(null);
        setMovePendingSiteId(0);
        setDetailOpen(false);
        setDetail(null);
        setItems((prev) => prev.filter((x) => x.id !== entryId));
        setToast(formatEntryRelocateMessage(r, targetLabel));
      } else {
        const hint =
          r.err === 'dedupe_conflict'
            ? r.detail || '目标站点已有相同金数据幂等键，无法迁入'
            : r.err === 'forbidden_site'
              ? '无权操作该站点'
              : r.err === 'move_failed'
                ? r.detail || '更换站点失败'
                : r.detail || r.err || '更换站点失败';
        setToast(hint);
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setToast(e instanceof Error ? e.message : '更换站点失败');
    } finally {
      setMoveBusyId(0);
      void load();
    }
  }

  async function confirmMoveFromSheet() {
    if (!moveSheetRow || movePendingSiteId < 1) return;
    const fromSiteId = moveSheetRow.site_id ?? currentSite?.id ?? 0;
    await moveEntryToSite(moveSheetRow.id, fromSiteId, movePendingSiteId, { skipConfirm: true });
  }

  async function deleteDetail() {
    if (!detail) return;
    if (!window.confirm('确定删除这条信息？删除后不可恢复。')) return;
    setSaving(true);
    try {
      const r = await apiFetch<{ ok: boolean }>('site_master_delete_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: detail.id }),
      });
      if (r.ok) {
        setDetailOpen(false);
        void load();
      } else {
        setToast('删除失败');
      }
    } catch {
      setToast('删除失败');
    } finally {
      setSaving(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="space-y-4">
      <Link
        to={siteMasterPaths.add}
        className="flex items-center justify-center gap-2.5 rounded-2xl bg-teal-600 py-4 text-xl font-bold text-white shadow-lg shadow-teal-600/25 ring-2 ring-teal-700/20 active:bg-teal-700"
      >
        <i className="fa-solid fa-plus text-lg" aria-hidden />
        添加新信息
      </Link>
      <p className="text-base leading-relaxed text-slate-600">
        列表中含「用户发布·待审核」条目时，可直接点「通过 / 拒绝」；也可进入详情核对图片与描述后再审核。
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setAuditFilter('all');
            setPage(1);
          }}
          className={cn(
            'rounded-full px-4 py-2 text-base font-semibold ring-1 transition',
            auditFilter === 'all'
              ? 'bg-teal-600 text-white ring-teal-700'
              : 'bg-white text-slate-700 ring-slate-200',
          )}
        >
          全部
        </button>
        <button
          type="button"
          onClick={() => {
            setAuditFilter('mp_user_pending');
            setPage(1);
          }}
          className={cn(
            'rounded-full px-4 py-2 text-base font-semibold ring-1 transition',
            auditFilter === 'mp_user_pending'
              ? 'bg-amber-500 text-white ring-amber-600'
              : 'bg-white text-slate-700 ring-slate-200',
          )}
        >
          待审核
          {pendingMpUserCount > 0 ? (
            <span className="ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-white/25 px-1.5 text-sm">
              {pendingMpUserCount}
            </span>
          ) : null}
        </button>
      </div>
      <div className="flex gap-2">
        <input
          className={`${inputClass} flex-1`}
          placeholder="搜索物品信息、电话"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <button
          type="button"
          onClick={() => {
            setAppliedQ(qInput.trim());
            setPage(1);
          }}
          className="shrink-0 rounded-2xl bg-teal-600 px-5 text-lg font-bold text-white"
        >
          搜
        </button>
      </div>

      {toast ? (
        <div
          className={`fixed inset-x-4 bottom-[calc(5.75rem+env(safe-area-inset-bottom))] z-[70] flex items-start gap-3 rounded-2xl px-4 py-3 text-lg shadow-lg ${
            isSiteMasterToastError(toast) ? 'bg-red-50 text-red-900' : 'bg-emerald-50 text-emerald-900'
          }`}
          role="status"
          aria-live="polite"
        >
          <p className="min-w-0 flex-1 leading-snug">{toast}</p>
          <button
            type="button"
            className="shrink-0 rounded-lg px-2 py-1 text-sm font-semibold text-slate-600 active:bg-black/5"
            aria-label="关闭提示"
            onClick={() => setToast('')}
          >
            关闭
          </button>
        </div>
      ) : null}
      {err ? <p className="rounded-2xl bg-red-50 px-4 py-3 text-lg text-red-900">{err}</p> : null}

      {loading ? (
        <Spinner label="加载信息" />
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-lg text-slate-500">
          {auditFilter === 'mp_user_pending' ? '暂无待审核的用户发布' : '暂无信息'}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((row) => {
            const pinBusy = pinBusyId === row.id;
            const soldBusy = soldBusyId === row.id;
            const moving = moveBusyId === row.id;
            const reviewBusy = reviewBusyId === row.id;
            const awaitingMpReview = isMpUserAwaitingReview(row);
            return (
              <li
                key={row.id}
                className={cn(
                  'overflow-hidden rounded-2xl border-2 bg-white',
                  row.sold_out ? 'border-slate-200 bg-slate-50/80' : 'border-slate-200'
                )}
              >
                <button
                  type="button"
                  onClick={() => void openDetail(row.id)}
                  className="w-full p-4 text-left active:bg-slate-50/80"
                >
                  <div className="mb-2 flex flex-wrap gap-2">
                    {allSitesMode && row.site_name ? (
                      <span className="inline-flex rounded-full bg-sky-50 px-2.5 py-0.5 text-sm font-medium text-sky-900 ring-1 ring-sky-200">
                        {row.site_name}
                      </span>
                    ) : null}
                    {awaitingMpReview ? (
                      <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-semibold text-amber-900 ring-1 ring-amber-300">
                        用户发布·待审核
                      </span>
                    ) : isMpUserEntry(row.entry_source) ? (
                      <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-0.5 text-sm font-medium text-violet-900 ring-1 ring-violet-200">
                        小程序发布
                      </span>
                    ) : null}
                    {row.is_sticky ? (
                      <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-semibold text-amber-900 ring-1 ring-amber-200">
                        {row.pin_status_text || '置顶'}
                      </span>
                    ) : row.sticky_expired ? (
                      <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 text-sm font-medium text-slate-600">
                        置顶已过期
                      </span>
                    ) : null}
                    {row.sold_out ? (
                      <span className="inline-flex rounded-full bg-slate-600 px-2.5 py-0.5 text-sm font-semibold text-white">
                        已售
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-0.5 text-sm font-medium text-emerald-800 ring-1 ring-emerald-200">
                        在售
                      </span>
                    )}
                  </div>
                  <p
                    className={cn(
                      'line-clamp-2 text-lg font-semibold',
                      row.sold_out ? 'text-slate-500' : 'text-slate-900'
                    )}
                  >
                    {row.body_text?.slice(0, 48) || row.title_text || `信息 #${row.id}`}
                  </p>
                  {awaitingMpReview && row.draft_errmsg ? (
                    <p className="mt-2 text-sm font-medium leading-snug text-amber-800">{row.draft_errmsg}</p>
                  ) : null}
                  {row.contact_phone || row.contact_wechat_id ? (
                    <p className="mt-2 text-base text-slate-600">
                      {row.contact_phone ? `电话：${row.contact_phone}` : null}
                      {row.contact_phone && row.contact_wechat_id ? ' · ' : null}
                      {row.contact_wechat_id ? `微信：${row.contact_wechat_id}` : null}
                    </p>
                  ) : null}
                  {moving ? (
                    <p className="mt-2 text-sm font-medium text-teal-800">正在更换站点，请稍候…</p>
                  ) : null}
                </button>
                <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50/60 px-3 py-2.5">
                  {awaitingMpReview ? (
                    <>
                      <button
                        type="button"
                        disabled={reviewBusy || pinBusy || soldBusy || moving}
                        onClick={() => void approveMpUserReview(row.id)}
                        className={cn(actionBtnClass, 'min-w-[6.5rem] border border-emerald-300 bg-emerald-600 text-white')}
                      >
                        <i className="fa-solid fa-check text-sm" aria-hidden />
                        {reviewBusy ? '…' : '通过'}
                      </button>
                      <button
                        type="button"
                        disabled={reviewBusy || pinBusy || soldBusy || moving}
                        onClick={() => void rejectMpUserReview(row.id)}
                        className={cn(actionBtnClass, 'min-w-[5.5rem] border border-red-200 bg-red-50 text-red-800')}
                      >
                        <i className="fa-solid fa-xmark text-sm" aria-hidden />
                        拒绝
                      </button>
                    </>
                  ) : null}
                  {sites.length > 1 ? (
                    <button
                      type="button"
                      disabled={pinBusy || soldBusy || moving}
                      onClick={() => openMoveSheetForRow(row)}
                      className={cn(actionBtnClass, 'min-w-[5.5rem] border border-violet-200 bg-violet-50 text-violet-900')}
                    >
                      <i className="fa-solid fa-arrow-right-arrow-left text-sm" aria-hidden />
                      换站点
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={pinBusy || soldBusy}
                    onClick={() => setPinSheetRow(row)}
                    className={cn(
                      actionBtnClass,
                      row.is_sticky
                        ? 'border border-amber-300 bg-amber-50 text-amber-900'
                        : 'border border-slate-200 bg-white text-slate-800'
                    )}
                  >
                    <i className="fa-solid fa-thumbtack text-sm" aria-hidden />
                    {pinBusy ? '…' : row.is_sticky ? `置顶·${row.sticky_remaining_days ?? 0}天` : '置顶'}
                  </button>
                  <button
                    type="button"
                    disabled={pinBusy || soldBusy}
                    onClick={() => void toggleSold(row)}
                    className={cn(
                      actionBtnClass,
                      row.sold_out
                        ? 'border border-slate-300 bg-white text-slate-700'
                        : 'border border-teal-200 bg-teal-600 text-white'
                    )}
                  >
                    <i
                      className={cn('fa-solid text-sm', row.sold_out ? 'fa-rotate-left' : 'fa-tag')}
                      aria-hidden
                    />
                    {soldBusy ? '…' : row.sold_out ? '恢复在售' : '已售'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex-1 rounded-2xl border-2 border-slate-300 py-3 text-lg font-semibold disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-lg text-slate-600">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="flex-1 rounded-2xl border-2 border-slate-300 py-3 text-lg font-semibold disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      ) : null}

      {moveSheetRow && sites.length > 1 ? (
        <div
          className="fixed inset-0 z-40 flex flex-col justify-end bg-black/40"
          role="dialog"
          aria-modal
          onClick={() => closeMoveSheet()}
        >
          <div
            className="max-h-[70vh] overflow-y-auto rounded-t-3xl bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-slate-900">更换站点</h3>
            <p className="mt-1 text-base text-slate-600">
              选择目标站点后点确认。将在目标站创建新信息，并删除原信息及其转发任务。
            </p>
            {moveBusyId === moveSheetRow.id ? (
              <p className="mt-3 text-center text-base font-medium text-teal-800">正在更换站点，请稍候…</p>
            ) : null}
            <ul className="mt-4 space-y-2">
              {sites
                .filter((s) => s.id !== (moveSheetRow.site_id ?? currentSite?.id))
                .map((s) => {
                  const selected = movePendingSiteId === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        disabled={moveBusyId === moveSheetRow.id}
                        onClick={() => setMovePendingSiteId(s.id)}
                        className={cn(
                          'w-full rounded-2xl border-2 px-4 py-3.5 text-left text-lg font-semibold disabled:opacity-50',
                          selected
                            ? 'border-teal-500 bg-teal-50 text-teal-950 ring-2 ring-teal-500/30'
                            : 'border-slate-200 bg-white text-slate-900 active:bg-slate-50'
                        )}
                      >
                        {s.site_name}
                        {selected ? (
                          <span className="mt-0.5 block text-sm font-medium text-teal-700">已选择</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
            </ul>
            <button
              type="button"
              disabled={moveBusyId === moveSheetRow.id || movePendingSiteId < 1}
              onClick={() => void confirmMoveFromSheet()}
              className="mt-4 w-full rounded-2xl bg-teal-600 py-3.5 text-lg font-bold text-white disabled:opacity-50"
            >
              {movePendingSiteId > 0
                ? `确认迁往「${sites.find((s) => s.id === movePendingSiteId)?.site_name ?? '目标站点'}」`
                : '请先选择目标站点'}
            </button>
            <button
              type="button"
              disabled={moveBusyId === moveSheetRow.id}
              className="mt-3 w-full rounded-2xl border-2 border-slate-200 py-3 text-lg font-semibold text-slate-600 disabled:opacity-50"
              onClick={() => closeMoveSheet()}
            >
              取消
            </button>
          </div>
        </div>
      ) : null}

      {pinSheetRow ? (
        <div
          className="fixed inset-0 z-40 flex flex-col justify-end bg-black/40"
          role="dialog"
          aria-modal
          aria-labelledby="pin-sheet-title"
          onClick={() => !pinBusyId && setPinSheetRow(null)}
        >
          <div
            className="rounded-t-3xl bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex items-center justify-between">
              <h3 id="pin-sheet-title" className="text-xl font-bold text-slate-900">
                置顶设置
              </h3>
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-lg text-slate-500"
                onClick={() => setPinSheetRow(null)}
                aria-label="关闭"
              >
                关闭
              </button>
            </div>
            <p className="mb-4 text-base text-slate-600">
              {pinSheetRow.is_sticky
                ? `当前：${pinSheetRow.pin_status_text || '置顶中'}，可续期或取消`
                : '选择置顶天数，信息将在小程序列表中优先展示'}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {PIN_PRESETS.map((d) => (
                <button
                  key={d}
                  type="button"
                  disabled={pinBusyId === pinSheetRow.id}
                  onClick={() => void applyPin(pinSheetRow.id, d)}
                  className="rounded-2xl border-2 border-teal-200 bg-teal-50 py-4 text-lg font-bold text-teal-900 active:bg-teal-100 disabled:opacity-50"
                >
                  {d} 天
                </button>
              ))}
            </div>
            {pinSheetRow.is_sticky ? (
              <button
                type="button"
                disabled={pinBusyId === pinSheetRow.id}
                onClick={() => void applyPin(pinSheetRow.id, 0)}
                className="mt-3 w-full rounded-2xl border-2 border-red-200 bg-red-50 py-3.5 text-lg font-semibold text-red-800 disabled:opacity-50"
              >
                取消置顶
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {detailOpen ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-white" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <header className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
            <button
              type="button"
              onClick={() => setDetailOpen(false)}
              className="rounded-xl border-2 border-slate-200 px-4 py-2 text-lg font-semibold"
            >
              返回
            </button>
            <h2 className="text-xl font-bold text-slate-900">
              {detail && isMpUserAwaitingReview(detail) ? '审核用户发布' : '编辑信息'}
            </h2>
          </header>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {detailLoading || !detail ? (
              <Spinner label="加载中" />
            ) : isMpUserAwaitingReview(detail) ? (
              <div className="space-y-4">
                <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-4">
                  <p className="text-lg font-semibold text-amber-950">用户通过小程序提交的闲置信息</p>
                  <p className="mt-2 text-base text-amber-900/90">请核对描述、图片与联系方式，确认无误后通过展示；不符合规范可拒绝。</p>
                </div>
                {detail.draft_errmsg ? (
                  <p className="rounded-xl bg-orange-50 px-4 py-3 text-base leading-relaxed text-orange-900 ring-1 ring-orange-200">
                    {detail.draft_errmsg}
                  </p>
                ) : null}
                {detail.type_label ? (
                  <p className="text-base text-slate-600">
                    分类：<span className="font-semibold text-slate-900">{detail.type_label}</span>
                  </p>
                ) : null}
                <div className="rounded-2xl border-2 border-slate-200 bg-white p-4">
                  <p className="whitespace-pre-wrap text-lg leading-relaxed text-slate-900">
                    {detail.body_text || detail.title_text || '（无描述）'}
                  </p>
                </div>
                {detail.contact_phone || detail.contact_wechat_id ? (
                  <p className="text-lg text-slate-700">
                    {detail.contact_phone ? `电话：${detail.contact_phone}` : null}
                    {detail.contact_phone && detail.contact_wechat_id ? ' · ' : null}
                    {detail.contact_wechat_id ? `微信：${detail.contact_wechat_id}` : null}
                  </p>
                ) : null}
                {(detail.images ?? []).length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {(detail.images ?? [])
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((im) => (
                        <img
                          key={im.id}
                          src={resolveMediaUrl(im.local_path)}
                          alt=""
                          className="aspect-square w-full rounded-xl border border-slate-200 object-cover"
                        />
                      ))}
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <button
                    type="button"
                    disabled={reviewBusyId === detail.id}
                    onClick={() => void approveMpUserReview(detail.id)}
                    className="rounded-2xl bg-emerald-600 py-4 text-xl font-bold text-white disabled:opacity-50"
                  >
                    {reviewBusyId === detail.id ? '处理中…' : '通过并展示'}
                  </button>
                  <button
                    type="button"
                    disabled={reviewBusyId === detail.id}
                    onClick={() => void rejectMpUserReview(detail.id)}
                    className="rounded-2xl border-2 border-red-300 bg-red-50 py-4 text-xl font-bold text-red-800 disabled:opacity-50"
                  >
                    拒绝
                  </button>
                </div>
              </div>
            ) : (
              <>
                <SiteMasterEntryForm
                  key={detail.id}
                  siteId={(detail as EntryDetail & { site_id?: number }).site_id ?? currentSite?.id ?? 0}
                  siteOptions={sites.length > 1 ? sites : undefined}
                  initialTypeKey={detail.type_key ?? ''}
                  initialBody={detail.body_text ?? ''}
                  initialPhone={detail.contact_phone ?? ''}
                  initialWechatId={detail.contact_wechat_id ?? ''}
                  initialImagePaths={(detail.images ?? [])
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((im) => im.local_path)
                    .filter(Boolean)}
                  submitLabel="保存修改"
                  saving={saving}
                  onSubmit={saveDetail}
                  footer={
                    <div className="space-y-3">
                      {sites.length > 1 ? (
                        <button
                          type="button"
                          disabled={saving || moveBusyId === detail.id}
                          onClick={() => openMoveSheetForRow(rowToMoveSheet(detail, currentSite?.id ?? 0))}
                          className="w-full rounded-2xl border-2 border-violet-200 bg-violet-50 py-4 text-xl font-bold text-violet-900 disabled:opacity-60"
                        >
                          更换站点
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void deleteDetail()}
                        className="w-full rounded-2xl border-2 border-red-300 bg-red-50 py-4 text-xl font-bold text-red-800 disabled:opacity-60"
                      >
                        删除此信息
                      </button>
                    </div>
                  }
                />
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
